import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import debounce from 'lodash/debounce'
import tzlookup from 'tz-lookup'

import AutoCompleteSearchField from 'modules/core/components/AutoCompleteSearchField'
import { GoogleMapsContext } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'

import { castError } from '../../castError'
import { WithCustomizationFieldsProps } from '../../withCustomizationFields'
import { WithFieldPermissionsProps } from '../../withFieldPermissions'

interface Props extends WithCustomizationFieldsProps, WithFieldPermissionsProps {
  fieldName: string
  label: string
  placeholder: string
  onPlaceSelected?: (place: { lat: number; lng: number }) => void
}

type PlaceAttributes = {
  address: string
  postcode: string
  state: string
  city: string
  country: string
  timeZone: string
  lat?: number
  lng?: number
  siret?: string
}

type Suggestion = {
  label: string
  value: string
}

// Extracted suggestions management logic
const useLocationSuggestions = ({
  inputRef,
  placesLibraryLoaded,
}: {
  inputRef: React.MutableRefObject<HTMLInputElement | null>
  placesLibraryLoaded: boolean
}) => {
  const [debouncedValue, setDebouncedValue] = useState<string>('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const {
    access: { locale: currentLocale },
  } = useCurrentSession()
  const locale = currentLocale.split('-')[0] ?? 'en'

  const debouncedOnSearch = useMemo(
    () =>
      debounce((val) => {
        setDebouncedValue(val)
      }, 500),
    []
  )

  useEffect(() => {
    if (!inputRef.current || typeof google === 'undefined' || !placesLibraryLoaded) return

    if (debouncedValue.length === 0) {
      setSuggestions([])
      return
    }

    new google.maps.places.AutocompleteService().getPlacePredictions(
      { input: debouncedValue, language: locale },
      (placeSuggestions) => {
        setSuggestions(
          placeSuggestions?.map((suggestion) => ({
            label: suggestion.description,
            value: suggestion.place_id,
          })) || []
        )
      }
    )
  }, [debouncedValue, placesLibraryLoaded, locale])

  const initPlaces = useCallback(() => {
    if (!placesLibraryLoaded) return

    inputRef.current?.addEventListener('input', () => {
      debouncedOnSearch(inputRef.current?.value || '')
    })
  }, [placesLibraryLoaded])

  initPlaces()

  const clearSuggestions = useCallback(() => {
    setSuggestions([])
  }, [])

  return {
    suggestions,
    clearSuggestions,
  }
}

const getPlaceAttributes = async (
  place: google.maps.places.PlaceResult
): Promise<PlaceAttributes> => {
  let address = place.name || ''
  let postcode = ''
  let city = ''
  let state = ''
  let country = ''
  const siret = ''
  const lat = place.geometry?.location?.lat()
  const lng = place.geometry?.location?.lng()

  if (!place.address_components) {
    return { address, postcode, state, city, country, lat, lng, timeZone: '', siret }
  }

  for (const component of place.address_components ?? []) {
    const componentType = component.types[0]

    switch (componentType) {
      case 'street_number': {
        break
      }

      case 'route': {
        address += `, ${component.short_name}`
        break
      }

      case 'postal_code': {
        postcode = `${component.long_name}${postcode}`
        break
      }

      case 'postal_code_suffix': {
        postcode = `${postcode}-${component.long_name}`
        break
      }

      case 'locality':
        address += `, ${component.short_name}`
        city = component.long_name
        break

      case 'administrative_area_level_1': {
        state = component.short_name
        break
      }

      case 'country':
        address += `, ${component.short_name}`
        country = component.short_name.toLowerCase()
        break
    }
  }

  const timeZone = lat && lng ? tzlookup(lat, lng) : ''

  return { address, postcode, state, city, country, lat, lng, timeZone, siret }
}

const LocationInput = ({
  isFieldMandatory,
  isFieldReadOnly,
  fieldName,
  label,
  placeholder,
  onPlaceSelected,
}: Props) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext()

  const { placesLibraryLoaded } = React.useContext(GoogleMapsContext)

  const inputRef = useLazyRef<HTMLInputElement | null>(() => null)
  const { suggestions, clearSuggestions } = useLocationSuggestions({
    inputRef,
    placesLibraryLoaded,
  })

  const onLocationSelected = (placeId: string): void => {
    if (!inputRef.current) {
      return
    }

    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      return
    }

    const request = { placeId, fields: ['geometry', 'address_components', 'name'] }

    const placeService = new google.maps.places.PlacesService(inputRef.current)

    placeService.getDetails(request, (result, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && result) {
        getPlaceAttributes(result)
          .then((placeAttributes) => {
            onPlaceSelected?.({
              lat: placeAttributes.lat ?? 0,
              lng: placeAttributes.lng ?? 0,
            })
          })
          .catch((error) => {
            throw new Error(`Error in getPlaceAttributes: ${error.message}`)
          })
      } else {
        throw new Error(`PlacesService status not OK: ${status}`)
      }
    })
  }

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => {
        const handleSelect = (result: { value: string; label: string }) => {
          setValue(fieldName, result.label, { shouldDirty: true, shouldValidate: true })
          onLocationSelected(result.value)
          clearSuggestions()
        }

        const handleChange = (value: string) => {
          setValue(fieldName, value)
        }

        const handleBlur = () => {
          setValue(fieldName, field.value, { shouldDirty: true, shouldValidate: true })
        }
        return (
          <AutoCompleteSearchField
            readonly={isFieldReadOnly}
            label={label}
            suggestions={suggestions}
            value={field.value}
            onSelect={handleSelect}
            placeholder={placeholder}
            getRef={(ref) => (ref ? (inputRef.current = ref) : null)}
            onChange={handleChange}
            onBlur={handleBlur}
            mandatory={isFieldMandatory}
            icon="pin"
            error={castError(errors?.[fieldName])}
          />
        )
      }}
    />
  )
}

export default LocationInput
